import { useState } from "react";
import { User, Users } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useAccountUsers, useDepartments } from "../useBriefing";

/**
 * RecipientPicker — choose a person OR a department from within the platform.
 * Stores { type, id, label } so the generator/flow can route internally.
 */
export default function RecipientPicker({ label, value, onChange }) {
  const [kind, setKind] = useState(value?.type || "user");
  const { data: users = [], isLoading: loadingUsers } = useAccountUsers();
  const { data: departments = [], isLoading: loadingDepts } = useDepartments();

  const options =
    kind === "user"
      ? users.map((u) => ({ id: u.id, label: u.fullName || u.name || u.email }))
      : departments.map((d) => ({ id: d.id, label: d.name }));
  const loading = kind === "user" ? loadingUsers : loadingDepts;

  function switchKind(next) {
    setKind(next);
    if (value && value.type !== next) onChange(null); // reset selection on switch
  }

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="flex flex-col gap-3 sm:flex-row">
        {/* Person / Department toggle */}
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
          <KindTab active={kind === "user"} onClick={() => switchKind("user")} icon={User}>
            Pessoa
          </KindTab>
          <KindTab
            active={kind === "department"}
            onClick={() => switchKind("department")}
            icon={Users}
          >
            Departamento
          </KindTab>
        </div>

        <div className="flex-1">
          <Select
            value={value?.id || ""}
            onValueChange={(id) => {
              const opt = options.find((o) => o.id === id);
              onChange({ type: kind, id, label: opt?.label || "" });
            }}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  loading
                    ? "Carregando…"
                    : `Selecionar ${kind === "user" ? "pessoa" : "departamento"}`
                }
              />
            </SelectTrigger>
            <SelectContent>
              {options.length === 0 && !loading ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  Nenhum {kind === "user" ? "usuário" : "departamento"} cadastrado.
                </div>
              ) : (
                options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function KindTab({ active, onClick, icon: Icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
        active
          ? "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}
